<template>
  <div
    :class="{
      'card-body py-3': !isInInquiry && !addNewCustomer,
      'form-readonly': readonly
    }">

    <h2
      v-if="readonly"
      class="heading-small text-grey mb-2">
      {{ $t('customerForm.customerData') }}
    </h2>

    <h2
      v-if="userType === 'customer'"
      class="heading-small text-grey mb-2">
      {{ $t('customerForm.myData') }}
    </h2>

    <div
      v-if="!allDataLoaded"
      class="loading-overlay">
      <div class="loading-spinner-wrapper is-embeded">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
    </div>

    <div :class="{'width-narrow mb-4': !readonly && !isInInquiry}">
      <validation-observer
        ref="customer-form"
        tag="form"
        class="customer-form">
        <div class="form-group">
          <label for="name">{{ $t('user.firstName') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <f-input
                v-model="data.name"
                :disabled="disabled || isInInquiryAdded"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
           <template v-else>
            {{ data.name }}
          </template>
        </div>

        <div class="form-group">
          <label for="surname">{{ $t('user.lastName') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required"
              name="surname"
              v-slot="{ errors }">
              <f-input
                v-model="data.surname"
                :disabled="disabled || isInInquiryAdded"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
           <template v-else>
            {{ data.surname }}
          </template>
        </div>

        <div class="form-group">
          <label for="email">{{ $t('user.emailAddress') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required|email"
              name="email"
              v-slot="{ errors }">
              <f-input
                v-model="data.email"
                type="email"
                :disabled="disabled || userType === 'customer' || isInInquiryAdded"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
           <template v-else>
            {{ data.email }}
          </template>
        </div>

        <div class="form-group">
          <label for="phone">{{ $t('user.phoneNumber') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required|min:9|tel"
              name="phone"
              mode="eager"
              v-slot="{ errors }">
              <vue-tel-input
                ref="tel-input"
                :key="defaultCountyCode"
                :defaultCountry="defaultCountyCode"
                :disabledFetchingCountry="true"
                :dynamicPlaceholder="false"
                :placeholder="''"
                :validCharactersOnly="true"
                :disabled="disabled || isInInquiryAdded"
                :inputOptions="{
                  showDialCode:true
                }"
                :class="{
                  'f-input form-control': true,
                  'is-valid': !errors.length,
                  'is-invalid': errors.length,
                  'is-disabled': disabled || isInInquiryAdded
                }"
                v-model="data.phone"
                :state="errors.length ? 'invalid' : null">
              </vue-tel-input>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
           <template v-else>
            {{ data.phone }}
          </template>
        </div>

        <div
          v-if="userType === 'customer'"
          class="form-group">
          <label for="address">{{ $t('user.address') }}</label>

          <validation-provider
            rules="required"
            name="address"
            v-slot="{ errors }">
            <f-input
              v-model="data.address"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="!isInInquiry && !addNewCustomer"
          class="form-group">
          <label for="city">{{ $t('user.city') }}</label>

          <template v-if="!readonly">
            <validation-provider
              :rules="userType === 'customer' ? 'required' : ''"
              name="city"
              v-slot="{ errors }">
              <f-input
                v-model="data.city"
                :disabled="disabled"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
           <template v-else>
             <template v-if="data.city">
               {{ data.city }}
             </template>
             <template v-else>
              {{ $t('ui.none') }}
             </template>
          </template>
        </div>

        <div
          v-if="!isInInquiry && !addNewCustomer"
          class="form-group">
          <label for="postal-code">{{ $t('user.postalCode') }}</label>

          <template v-if="!readonly">
            <template v-if="langId === 2">
              <validation-provider
                :rules="userType === 'customer' ? 'required|postal-code-gb' : 'postal-code-gb'"
                name="postal-code"
                v-slot="{ errors }">
                <f-input
                  v-model="data.post_code"
                  :disabled="disabled"
                  :state="errors.length ? 'invalid' : null"/>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </template>
            <template v-else>
              <validation-provider
                :rules="userType === 'customer' ? 'required|postal-code' : 'postal-code'"
                name="postal-code"
                v-slot="{ errors }">
                <f-input
                  v-model="data.post_code"
                  v-mask="'##-###'"
                  :disabled="disabled"
                  :state="errors.length ? 'invalid' : null"/>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </template>
          </template>
           <template v-else>
             <template v-if="data.post_code">
               {{ data.post_code }}
             </template>
             <template v-else>
              {{ $t('ui.none') }}
             </template>
          </template>
        </div>

        <div
          v-if="userType === 'adviser' && !readonly"
          class="form-group">
          <validation-provider
            name="send-invitation"
            v-slot="{ errors }">
            <f-checkbox
              v-model="data.send_invitation"
              :state="errors.length ? 'invalid' : null">
              {{ $t('customer.sendInvitation') }}
            </f-checkbox>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

      </validation-observer>
    </div>

    <div
      v-if="!readonly"
      :class="{'text-right border-top pt-3': userType === 'customer'}">
      <f-button
        :class="{'full-width': isInInquiry || addNewCustomer}"
        :disabled="isInInquiryAdded || isInSettings || disabled"
        theme="primary"
        big
        :withArrow="!isInSettings && userType !== 'customer'"
        @click.prevent="validateForm">
        <template v-if="isInInquiryAdded">
          {{ $t('ui.added') }}
        </template>
        <template v-else-if="userType === 'customer'">
          {{ $t('ui.save') }}
        </template>
        <template v-else>
          {{ $t('ui.next') }}
        </template>
      </f-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'customer-form',
  props: {
    isInInquiry: {
      type: Boolean,
      default: false
    },
    isInSettings: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: true
    },
    addNewCustomer: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    id: {
      type: Number,
      default: 0
    },
    name: {
      type: String,
      default: ''
    },
    surname: {
      type: String,
      default: ''
    },
    email: {
      type: String,
      default: ''
    },
    phone: {
      type: String,
      default: ''
    },
    city: {
      type: String,
      default: ''
    },
    postCode: {
      type: String,
      default: ''
    },
    address: {
      type: String,
      default: ''
    },
    acceptedTermsAndPolicy: {
      type: Boolean,
      default: false
    },
    contactAdviserAgreement: {
      type: Boolean,
      default: false
    },
    contactFinhubAgreement: {
      type: Boolean,
      default: false
    },
    sendInvitation: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters({
      langId: 'getUserLanguageId'
    }),
    allDataLoaded () {
      return this.loaded && this.defaultCountyCode;
    },
    isEdit () {
      if (this.addNewCustomer) {
        return false;
      }
      if (this.readonly) {
        return false;
      }

      if (!this.readonly) {
        return true;
      }

      return !!this.$route.params.id;
    },
    isLanguageSet () {
      if (this.$store.getters['getUserHasLanguage']) {
        return true;
      }

      return false;
    },
    isLanguageError () {
      if (this.$store.getters['getUserLanguageHasError']) {
        return true;
      }

      return false;
    }
  },
  watch: {
    isLanguageError (newValue) {
      if (!newValue) {
        return;
      }
      Vue.nextTick(() => {
        Vue.set(this, 'defaultCountyCode', this.$store.getters['getUserPhoneCountryCode']);
      })
    },
    isLanguageSet (newValue) {
      if (!newValue) {
        return;
      }

      Vue.nextTick(() => {
        Vue.set(this, 'defaultCountyCode', this.$store.getters['getUserPhoneCountryCode']);
      })
    }
  },
  data() {
    return {
      data: {
        id: 0,
        name: '',
        surname: '',
        email: '',
        phone: '',
        city: '',
        post_code: '',
        address: '',
        send_invitation: true
      },
      loaded: false,
      loadError: false,
      userType: null,
      isInInquiryAdded: false,
      defaultCountyCode: null
    };
  },
  mounted () {
    if ((this.$store.getters['getUserHasLanguage'] || this.$store.getters['getUserLanguageHasError']) && !this.defaultCountyCode) {
      Vue.set(this, 'defaultCountyCode', this.$store.getters['getUserPhoneCountryCode']);
    }
    this.userType = this.$store.getters['getUserType'].toLowerCase();

    if (this.isEdit || this.readonly) {
      this.data.id = this.id;
      this.data.name = this.name;
      this.data.surname = this.surname;
      this.data.email = this.email;
      this.data.phone = this.phone;
      this.data.city = this.city;
      this.data.post_code = this.postCode;
      this.data.address = this.address;
      this.data.send_invitation = this.sendInvitation;
    }

    this.loaded = true;
  },
  methods: {
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();

      let endpointUrl = '';

      if (this.userType === 'adviser') {
        endpointUrl = '/api/adviser/customers/store';
      } else {
        endpointUrl = '/api/customer/customer/update';
      }

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.userType === 'adviser' ? this.$t('customerForm.customerAdded') : this.$t('ui.savedChanges'),
        successText: this.userType === 'adviser' ? this.$t('customerForm.newCustomerAdded') : this.$t('customerAgreementsForm.yourProfileHasBeenUpdate'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.userType === 'adviser' ? this.$t('customerForm.newCustomerAddedError') : this.$t('customerAgreementsForm.updateProfileError'),
        successAction: () => {
          if (this.userType === 'customer') {
            this.$bus.$emit('customer-settings-reload');
            return;
          }
          if (!this.isInInquiry) {
            this.$router.push('/klienci');
          } else {
            this.isInInquiryAdded = true;
            this.$bus.$emit('inquiry-form-set-customer', formDataToSend);
          }
        }
      });
    },
    prepareFormDataToSend () {
      let telInput = this.$refs['tel-input'];
      let formDataToSend = {
        name: this.data.name,
        surname: this.data.surname,
        email: this.data.email,
        phone: this.data.phone,
        city: this.data.city,
        post_code: this.data.post_code
      };

      if (telInput) {
        formDataToSend.phone = telInput.phoneObject.number.international;
      }

      if (this.userType === 'customer') {
        formDataToSend.address = this.data.address;
        formDataToSend.accepted_terms_and_policy_privacy = this.acceptedTermsAndPolicy ? 1 : 0;
        formDataToSend.contact_adviser_agreement = this.contactAdviserAgreement ? 1 : 0;
        formDataToSend.contact_finhub_agreement = this.contactFinhubAgreement ? 1 : 0;
      } else {
        formDataToSend.send_invitation = +this.data.send_invitation;
      }

      return formDataToSend;
    },
    validateForm () {
      FormUtils.validate(this.$refs['customer-form'], this.submitForm);
    },
  }
}
</script>
